'use client'

import type React from 'react'
import { Button } from '@/components/ui/common/Button'
import { cn } from '@/utils'
import { Check, ChevronDown, ChevronUp, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { SequencingTask } from '@/api/services/types/questionTypes'
import { useTestResponse } from '@/hooks/useTestResponse'

interface SequenceItem {
  id: string
  image: string
  title: string
  correctPosition: number
}

interface SequenceTaskProps {
  data: SequencingTask
  moduleId: string
  questionId: string
  onComplete?: (isCorrect: boolean) => void
  onStart?: () => void
  timeLimitSeconds?: number | null
}

export function SequenceTask({
  data,
  moduleId,
  questionId,
  onComplete,
  onStart,
  timeLimitSeconds
}: SequenceTaskProps) {
  const { addQuestionResponse } = useTestResponse()
  const [sequence, setSequence] = useState<SequenceItem[]>([])
  const [result, setResult] = useState<'correct' | 'incorrect' | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [positions, setPositions] = useState<Record<string, string>>({})
  const [remainingTime, setRemainingTime] = useState<number | null>(timeLimitSeconds ?? null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  // Timer effect
  useEffect(() => {
    setRemainingTime(timeLimitSeconds ?? null)
  }, [timeLimitSeconds])

  useEffect(() => {
    if (!timeLimitSeconds) return

    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev === null || prev <= 0) {
          clearInterval(timer)
          // Automatyczne sprawdzenie odpowiedzi po zakończeniu czasu
          checkSequence()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [timeLimitSeconds])

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // Inicjalizacja sekwencji w losowej kolejności
  useEffect(() => {
    const items: SequenceItem[] = data.images.map(img => ({
      id: img.id,
      image: `https://api.pathadvisor.pl/api/v1/files/${img.imageFileId}`,
      title: `Obraz ${img.correctSequencePosition}`,
      correctPosition: img.correctSequencePosition
    }))

    const shuffled = [...items].sort(() => Math.random() - 0.5)
    setSequence(shuffled)
    itemRefs.current = itemRefs.current.slice(0, shuffled.length)

    // Inicjalizacja pustych pozycji dla trybu wpisywania numerów
    if (data.sequencingMode === 'INPUT') {
      const initialPositions: Record<string, string> = {}
      shuffled.forEach((item) => {
        initialPositions[item.id] = ''
      })
      setPositions(initialPositions)
    }

    if (onStart) {
      onStart()
    }
  }, [data, onStart])

  // Aktualizacja pozycji w trybie wpisywania numerów
  const handlePositionChange = (id: string, value: string) => {
    if (data.sequencingMode === 'INPUT') {
      // Pozwól tylko na cyfry 1-5
      if (value === '' || /^[1-5]$/.test(value)) {
        setPositions((prev) => ({
          ...prev,
          [id]: value
        }))
      }
    }
  }

  // Funkcja do zmiany pozycji elementu
  const moveItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= sequence.length) return

    const newSequence = [...sequence]
    const [movedItem] = newSequence.splice(fromIndex, 1)
    newSequence.splice(toIndex, 0, movedItem)

    setSequence(newSequence)
  }

  // Obsługa przeciągania
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    if (data.sequencingMode !== 'DRAG') return
    e.dataTransfer.setData('text/plain', index.toString())

    // Ustawienie obrazu przeciągania
    const draggedElement = itemRefs.current[index]
    if (draggedElement) {
      const rect = draggedElement.getBoundingClientRect()
      const offsetX = e.clientX - rect.left
      const offsetY = e.clientY - rect.top

      e.dataTransfer.setDragImage(draggedElement, offsetX, offsetY)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    if (data.sequencingMode !== 'DRAG') return
    e.preventDefault()
    setDragOverIndex(index)
  }

  const handleDragLeave = () => {
    setDragOverIndex(null)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, toIndex: number) => {
    if (data.sequencingMode !== 'DRAG') return
    e.preventDefault()
    const fromIndex = Number.parseInt(e.dataTransfer.getData('text/plain'))

    if (!isNaN(fromIndex) && fromIndex !== toIndex) {
      moveItem(fromIndex, toIndex)
    }

    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDragOverIndex(null)
  }

  // Sprawdzanie poprawności
  const checkSequence = async () => {
    let isCorrect: boolean
    let sequenceOrder: string[] = []

    if (data.sequencingMode === 'DRAG') {
      isCorrect = sequence.every((item, index) => item.correctPosition === index + 1)
      sequenceOrder = sequence.map(item => item.id)
      
      // Zapisz odpowiedź w kontekście testu dla trybu DRAG
      await addQuestionResponse(moduleId, {
        questionId,
        questionType: 'SEQUENCING_TASK',
        sequencingTaskAnswer: {
          sequenceOrder,
          responseTimeMillis: Date.now()
        }
      })
    } else {
      // Sprawdź, czy wszystkie pozycje są wypełnione
      const allFilled = Object.values(positions).every((pos) => pos !== '')
      if (!allFilled) {
        alert('Wypełnij wszystkie pozycje przed sprawdzeniem!')
        return
      }

      // Sprawdź, czy pozycje są unikalne
      const uniquePositions = new Set(Object.values(positions))
      if (uniquePositions.size !== sequence.length) {
        alert('Każda pozycja musi być unikalna (1-5)!')
        return
      }

      // Sprawdź poprawność
      isCorrect = sequence.every((item) => {
        const position = Number.parseInt(positions[item.id])
        return position === item.correctPosition
      })

      // Przygotuj kolejność sekwencji
      sequenceOrder = Object.entries(positions)
        .sort(([, a], [, b]) => Number(a) - Number(b))
        .map(([id]) => id)
    }

    setResult(isCorrect ? 'correct' : 'incorrect')

    if (onComplete) {
      onComplete(isCorrect)
    }
  }

  // Resetowanie zadania
  const resetTask = () => {
    const shuffled = [...sequence].sort(() => Math.random() - 0.5)
    setSequence(shuffled)
    setResult(null)

    if (data.sequencingMode === 'INPUT') {
      // Resetowanie pozycji
      const initialPositions: Record<string, string> = {}
      shuffled.forEach((item) => {
        initialPositions[item.id] = ''
      })
      setPositions(initialPositions)
    }
  }

  return (
    <div className="relative mx-auto w-full max-w-md rounded-lg bg-white p-6 shadow-md">
      {remainingTime !== null && (
        <div className="absolute -top-8 right-0 text-sm font-medium text-gray-600 bg-transparent px-3 py-1 rounded-md">
          Pozostały czas: {formatTime(remainingTime)}
        </div>
      )}
      <h2 className="mb-4 text-center text-xl font-bold">
        Ustaw obrazki we właściwej kolejności
      </h2>

      <div className="mb-6 space-y-2">
        {sequence.map((item, index) => (
          <div
            className={cn(
              'flex items-center rounded-lg border p-2 transition-all',
              dragOverIndex === index && data.sequencingMode === 'DRAG' && 'border-indigo-500 bg-indigo-50',
              result === 'correct' && 'border-green-500',
              result === 'incorrect' && 'border-red-500'
            )}
            draggable={data.sequencingMode === 'DRAG'}
            key={item.id}
            onDragEnd={data.sequencingMode === 'DRAG' ? handleDragEnd : undefined}
            onDragLeave={data.sequencingMode === 'DRAG' ? handleDragLeave : undefined}
            onDragOver={data.sequencingMode === 'DRAG' ? (e) => handleDragOver(e, index) : undefined}
            onDragStart={data.sequencingMode === 'DRAG' ? (e) => handleDragStart(e, index) : undefined}
            onDrop={data.sequencingMode === 'DRAG' ? (e) => handleDrop(e, index) : undefined}
            ref={(el) => {
              if (el) itemRefs.current[index] = el
            }}
          >
            {data.sequencingMode === 'DRAG' ? (
              <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100">
                {index + 1}
              </div>
            ) : (
              <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100">
                <input
                  className="h-6 w-6 border-none p-0 text-center focus:ring-0"
                  disabled={result !== null}
                  maxLength={1}
                  onChange={(e) => handlePositionChange(item.id, e.target.value)}
                  type="text"
                  value={positions[item.id] || ''}
                />
              </div>
            )}

            <div className="mr-3 h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border border-gray-200">
              <img
                alt={item.title}
                className="h-full w-full object-cover object-center"
                src={item.image}
              />
            </div>

            <div className="flex-1">{item.title}</div>

            {data.sequencingMode === 'DRAG' && (
              <div className="ml-2 flex flex-col">
                <button
                  aria-label="Przesuń w górę"
                  className="p-1 text-gray-500 transition-colors hover:text-indigo-600"
                  disabled={result !== null}
                  onClick={() => moveItem(index, index - 1)}
                  type="button"
                >
                  <ChevronUp size={16} />
                </button>
                <button
                  aria-label="Przesuń w dół"
                  className="p-1 text-gray-500 transition-colors hover:text-indigo-600"
                  disabled={result !== null}
                  onClick={() => moveItem(index, index + 1)}
                  type="button"
                >
                  <ChevronDown size={16} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="mb-4 text-center text-sm text-gray-600">
        {data.sequencingMode === 'DRAG'
          ? 'Przeciągnij obrazki, aby zmienić ich kolejność lub użyj strzałek.'
          : 'Wpisz numery od 1 do 5, aby określić kolejność obrazków.'}
      </p>

      <div className="flex justify-center space-x-4">
        {result === null ? (
          <Button className="bg-[#2bcaca] hover:bg-[#2b9999]" onClick={checkSequence}>
            Sprawdź
          </Button>
        ) : (
          <>
            <div
              className={cn(
                'flex items-center rounded-md px-4 py-2',
                result === 'correct'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              )}
            >
              {result === 'correct' ? (
                <>
                  <Check className="mr-2" size={18} />
                  <span>Poprawnie!</span>
                </>
              ) : (
                <>
                  <X className="mr-2" size={18} />
                  <span>Niepoprawnie. Spróbuj ponownie.</span>
                </>
              )}
            </div>
            <Button onClick={resetTask} variant="outline">
              Resetuj
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
